/**
 * What the learner was last doing in Practice, remembered across visits.
 *
 * Every generic way into a topic — the map's topic bubbles, the sidebar's topic
 * list, `/fide-test-examples` — has to guess a view, and every level-less URL has
 * to guess a level. These two values turn those guesses into "what you were doing
 * last time". An explicit choice (a URL that names a view or a level) always wins;
 * this is only ever the fallback.
 *
 * localStorage rather than a cookie: nothing server-side reads it, and a cookie
 * would drag the consent banner into a pure UI preference. Not split per language
 * either — "I practise discussion" and "I'm B1" describe the learner, not French
 * vs German, and the other practice memories here are language-agnostic too.
 */

import { isReactSnap } from '../bootstrap/bootRuntime';

export const PRACTICE_VIEW_KEY = 'practice:lastView';
export const PRACTICE_LEVEL_KEY = 'practice:lastLevel';
export const PRACTICE_LISTENING_LEVEL_KEY = 'practice:lastListeningLevel';
export const PRACTICE_TOPIC_KEY = 'practice:lastTopicId';
export const PRACTICE_MAP_VIEW_KEY = 'practice:lastMapView';
export const PRACTICE_RAIL_HIDDEN_KEY = 'practice:railHidden';
export const PRACTICE_MATERIAL_HIDDEN_KEY = 'practice:materialHidden';

const PRACTICE_VIEWS = ['dialogue', 'discussion', 'vocab', 'flashcards'];
// Mirrors PRACTICE_LEVELS in pages/Practice/practiceConfig.js. Inlined so this
// module stays in utils/ (App.js and languageRouting.js read it) without importing
// a page module.
const PRACTICE_LEVEL_IDS = ['a1', 'a2', 'b1'];

/** Storage can be absent (SSR/react-snap) or throw (Safari private mode). */
export const readStoredValue = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  if (typeof window === 'undefined' || !value) return;
  // react-snap prerenders every route through one browser, so these memories would
  // carry from one prerendered page into the next — a level page rendering the topic
  // rail, and `/{lang}/fide-practice` forwarding to the catalog. The build must see a
  // first-time visitor on every page, so nothing is remembered while prerendering.
  if (isReactSnap()) return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* storage unavailable — the caller's default still applies */
  }
};

/** The last view practised, or null when nothing valid is stored. */
export const readLastPracticeView = () => {
  const stored = readStoredValue(PRACTICE_VIEW_KEY);
  return PRACTICE_VIEWS.includes(stored) ? stored : null;
};

export const writeLastPracticeView = (view) => {
  if (PRACTICE_VIEWS.includes(view)) write(PRACTICE_VIEW_KEY, view);
};

/**
 * The last view, narrowed to the two a topic can be *entered* on. Vocabulary and
 * flashcards are remembered (they keep tab-to-tab moves on the same tab) but must
 * not redirect a topic entry — picking a theme is asking to practise it.
 */
export const readLastPracticeTrack = () => {
  const view = readLastPracticeView();
  return view === 'dialogue' || view === 'discussion' ? view : null;
};

export const readLastPracticeLevel = () => {
  const stored = readStoredValue(PRACTICE_LEVEL_KEY);
  return PRACTICE_LEVEL_IDS.includes(stored) ? stored : null;
};

export const writeLastPracticeLevel = (levelId) => {
  if (PRACTICE_LEVEL_IDS.includes(levelId)) write(PRACTICE_LEVEL_KEY, levelId);
};

/** Listening's level is kept apart: a B1 speaker may well listen at A2. */
export const readLastListeningLevel = () => {
  const stored = readStoredValue(PRACTICE_LISTENING_LEVEL_KEY);
  return PRACTICE_LEVEL_IDS.includes(stored) ? stored : null;
};

export const writeLastListeningLevel = (levelId) => {
  if (PRACTICE_LEVEL_IDS.includes(levelId)) write(PRACTICE_LISTENING_LEVEL_KEY, levelId);
};

/**
 * Which of the map's two tabs the learner was last in — the level journey
 * (`/{lang}/fide-practice`) or the topic catalog (`/{lang}/fide-test-topics`). A bare
 * practice URL forwards to the remembered one instead of always resetting to the level map.
 *
 * A crawler has no localStorage, so it always sees the level view — the same HTML
 * react-snap captures — and nothing about the indexed page changes. This is a
 * returning-visitor convenience layered on top of it.
 */
const MAP_VIEWS = ['level', 'topic'];

export const readLastMapView = () => {
  const stored = readStoredValue(PRACTICE_MAP_VIEW_KEY);
  return MAP_VIEWS.includes(stored) ? stored : null;
};

export const writeLastMapView = (view) => {
  if (MAP_VIEWS.includes(view)) write(PRACTICE_MAP_VIEW_KEY, view);
};

/**
 * Which tab a practice URL opens on, given whatever `?view=` says.
 *
 * `?view=` is the retired way of asking for a tab — kept because old links carry it —
 * and it always wins, including a stale or misspelled one, which means the level map,
 * not the remembered tab. Only a bare `/{lang}/fide-practice` consults the memory.
 */
export const resolveMapView = (rawView) => {
  if (rawView === 'topic') return 'topic';
  if (rawView) return 'level';
  return readLastMapView() ?? 'level';
};

/**
 * Whether the learner has collapsed the practice navigation, per surface: `rail` is
 * the desktop sidebar, `material` the map's material picker. The phone's bottom bar
 * is not remembered: it reopens on every page.
 *
 * Stored as '1'/'0' rather than true/false: `write` ignores empty values, and '0'
 * has to survive so "I want this shown" is remembered as firmly as "hide it".
 */
const CHROME_KEY = {
  rail: PRACTICE_RAIL_HIDDEN_KEY,
  material: PRACTICE_MATERIAL_HIDDEN_KEY,
};

/**
 * What each surface does before the learner has said anything.
 *
 * The desktop rail starts open: it should be visible before it is looked for.
 *
 * The material picker is the exception, and starts collapsed. It asks a second
 * question ("which material?") above the one the topic catalog is for ("which
 * topic?"), and answering both before anything can be opened is what made that page
 * read as a form. Collapsed it is one quiet line naming the material in force, which
 * is enough to notice and change; expanded, it stays expanded.
 */
const CHROME_DEFAULT_HIDDEN = { rail: false, material: true };

export const readChromeHidden = (surface) => {
  const stored = readStoredValue(CHROME_KEY[surface]);
  return stored === null || stored === undefined ? CHROME_DEFAULT_HIDDEN[surface] === true : stored === '1';
};

export const writeChromeHidden = (surface, hidden) => {
  if (CHROME_KEY[surface]) write(CHROME_KEY[surface], hidden ? '1' : '0');
};

/** The last catalog topic opened, so "By topic" can drop you back into it. */
export const readLastTopicId = () => readStoredValue(PRACTICE_TOPIC_KEY);

export const writeLastTopicId = (topicId) => write(PRACTICE_TOPIC_KEY, topicId);

/**
 * When the phone's contents pill is highlighted. At most five times per tab session,
 * and never again once the learner has opened the drawer three times. Like the other
 * memories here, the openings stay on this device and identify no one, so they need
 * no consent. The session count is held in memory when sessionStorage is unavailable.
 */
export const PRACTICE_CONTENTS_HINT_SESSION_KEY = 'practice:contentsHintsShown';
export const PRACTICE_CONTENTS_OPENS_KEY = 'practice:contentsOpens';
const CONTENTS_HINT_SESSION_LIMIT = 5;
const CONTENTS_OPENS_TO_STOP_HINT = 3;
let memoryHintsShown = 0;

const readHintsShown = () => {
  try {
    return Number.parseInt(window.sessionStorage.getItem(PRACTICE_CONTENTS_HINT_SESSION_KEY), 10) || 0;
  } catch {
    return memoryHintsShown;
  }
};

const readContentsOpens = () => Number.parseInt(readStoredValue(PRACTICE_CONTENTS_OPENS_KEY), 10) || 0;

export const shouldShowContentsHint = () =>
  typeof window !== 'undefined' &&
  !isReactSnap() &&
  readHintsShown() < CONTENTS_HINT_SESSION_LIMIT &&
  readContentsOpens() < CONTENTS_OPENS_TO_STOP_HINT;

export const recordContentsHintShown = () => {
  if (typeof window === 'undefined' || isReactSnap()) return;
  const shown = readHintsShown() + 1;
  try {
    window.sessionStorage.setItem(PRACTICE_CONTENTS_HINT_SESSION_KEY, String(shown));
  } catch {
    memoryHintsShown = shown;
  }
};

/** Any opening of the drawer from its pill, in any section. */
export const recordContentsOpened = () => write(PRACTICE_CONTENTS_OPENS_KEY, readContentsOpens() + 1);
